import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isEqual, join, keysIn, reduce } from 'lodash-es';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { getIcuParameters } from '@nimbus-library/features/localization/utils/utils';
import { LocalizableDependentError, LocalizableError } from '@nimbus-library/features/shared/models/localizable-error.model';

export type IValidationMessagesItem =
  | string
  | ((validationName: string, validationDescriptor: any, translateService: TranslateService) => string);
export type IValidationMessagesItemMap = IMap<IValidationMessagesItem>;

export class InputValidator {
  private static defaultValidationMessages: IValidationMessagesItemMap = {
    required: (name, validationDescriptor, translateService) => translateService.instant(TranslationKeys.GeneralValidatorRequired),
    requiredDndInputItem: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorRequiredDndInputItem),
    min: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorMin, { value: validationDescriptor.min }),
    max: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorMax, { value: validationDescriptor.max }),
    maxLength: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorMaxLength, {
        value: validationDescriptor?.requiredLength ?? validationDescriptor,
      }),
    maxlength: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorMaximumLength, {
        value: validationDescriptor?.requiredLength ?? validationDescriptor,
      }),
    unique: (name, validationDescriptor, translateService) => translateService.instant(TranslationKeys.GeneralValidatorUnique),
    AboveLimit: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorMax, { value: validationDescriptor.max }),
    BelowLimit: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorMin, { value: validationDescriptor.min }),
    Required: (name, validationDescriptor, translateService) => translateService.instant(TranslationKeys.GeneralValidatorRequired),
    invalidEmailAddress: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidEmailAddress),
    invalidSIPAddress: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidSIPAddress),
    invalidGUID: (name, validationDescriptor, translateService) => translateService.instant(TranslationKeys.GeneralValidatorInvalidGUID),
    invalidDomain: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidDomain),
    invalidUrl: (name, validationDescriptor, translateService) => translateService.instant(TranslationKeys.GeneralValidatorInvalidUrl),
    invalidPhoneNumber: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidPhoneNumber),
    invalidFileType: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidFileType, { value: validationDescriptor }),
    invalidFileDuration: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidFileDuration, { value: validationDescriptor }),
    invalidFileSize: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidFileSize, { value: validationDescriptor }),
    duplicateDomain: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorDuplicateDomain),
    valueCanNotBeEmpty: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorValueCanNotBeEmpty),
    serviceUpnNotFound: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorServiceUpnNotFound),
    invalidApiKeySid: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.ManageProviderConnectorsApiKeySidValidation),
    atLeastOneExitIntent: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.ManageVirtualUserAtLeastOneExitIntent),
    invalidIpFormat: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.GeneralValidatorInvalidIPFormat),
  };

  private currentErrors: ValidationErrors;

  private lastErrorString: string;

  public constructor(
    private validationMessages: IValidationMessagesItemMap,
    private readonly translateService: TranslateService,
  ) {}

  public getErrorMessage(errors: ValidationErrors): string {
    if (!isEqual(errors, this.currentErrors)) {
      this.lastErrorString = errors
        ? join(
            reduce(
              keysIn(errors),
              (acc, key) => {
                const message = (this.validationMessages && this.validationMessages[key]) || InputValidator.defaultValidationMessages[key];
                if (message) {
                  acc.push(typeof message === 'function' ? message(key, errors[key], this.translateService) : (message as string));
                } else if (errors[key] instanceof LocalizableError) {
                  const localizableError = errors[key];
                  acc.push(
                    this.translateService.instant(
                      localizableError.translateKey,
                      getIcuParameters(localizableError.translateKey as TranslationKeys, localizableError.pluralForm, {
                        value: localizableError.value,
                      }),
                    ),
                  );
                } else if (errors[key] instanceof LocalizableDependentError) {
                  const localizableError = errors[key] as LocalizableDependentError;
                  let errorMessage = '';
                  localizableError.dependencies.forEach((dependency) => {
                    const resultDependencies = dependency.dependentItems.join(', ');
                    errorMessage = `${errorMessage || ''}${this.translateService.instant(localizableError.translateKey, {
                      item: dependency.item,
                      items: resultDependencies,
                    })}; `;
                  });
                  acc.push(errorMessage);
                } else {
                  acc.push(`Unknown error (Code: ${key})`);
                }
                return acc;
              },
              [],
            ),
            ' ',
          )
        : '';
    }
    return this.lastErrorString;
  }
}
